import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { assistantService, authService } from '../utils/api';
import { LLM_MODEL_OPTIONS, LLM_PROVIDER_OPTIONS } from '../utils/llmOptions';

function clampNumber(n, min, max) {
  const v = Number(n);
  if (Number.isNaN(v)) return min;
  return Math.min(max, Math.max(min, v));
}

function LLMSettings() {
  const { assistantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [form, setForm] = useState({
    llm_provider: 'openai',
    llm_model: '',
    llm_max_tokens: '',
    llm_temperature: 0.7
  });

  const userRole = authService.getUserRole();
  const canEdit = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await assistantService.getAssistantById(assistantId);
        if (!isMounted) return;
        setAssistant(data);
        setForm({
          llm_provider: data.llm_provider || 'openai',
          llm_model: data.llm_model || '',
          llm_max_tokens: data.llm_max_tokens ?? '',
          llm_temperature: data.llm_temperature ?? 0.7
        });
      } catch (e) {
        toast.error(e?.message || 'Failed to load assistant LLM settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (assistantId) load();
    return () => {
      isMounted = false;
    };
  }, [assistantId]);

  const title = useMemo(
    () => assistant?.assistant_name || assistant?.name || 'Assistant',
    [assistant]
  );

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const temperatureValue = useMemo(
    () => Math.round(clampNumber(form.llm_temperature, 0, 2) * 10) / 10,
    [form.llm_temperature]
  );

  const handleSave = async () => {
    try {
      setSaving(true);

      const maxTokensNum =
        form.llm_max_tokens === '' ? undefined : clampNumber(form.llm_max_tokens, 0, 1_000_000);

      const payload = {
        llm_provider: form.llm_provider || 'openai',
        llm_model: form.llm_model || undefined,
        llm_max_tokens: maxTokensNum,
        llm_temperature: temperatureValue
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const updated = await assistantService.updateAssistant(assistantId, payload);
      toast.success('LLM settings saved');

      const selected = assistantService.getSelectedAssistant();
      if (selected?.id === assistantId) {
        assistantService.setSelectedAssistant({ ...selected, ...updated });
      }
    } catch (e) {
      toast.error(e?.message || e?.detail || 'Failed to save LLM settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              LLM
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {title}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled>
                      <InputLabel>LLM Provider</InputLabel>
                      <Select
                        value={form.llm_provider}
                        label="LLM Provider"
                        onChange={(e) => handleChange('llm_provider', e.target.value)}
                      >
                        {LLM_PROVIDER_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={!canEdit}>
                      <InputLabel>LLM Model</InputLabel>
                      <Select
                        value={form.llm_model}
                        label="LLM Model"
                        onChange={(e) => handleChange('llm_model', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {LLM_MODEL_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max tokens"
                      value={form.llm_max_tokens}
                      onChange={(e) => handleChange('llm_max_tokens', e.target.value)}
                      disabled
                      inputProps={{ min: 0, step: 1 }}
                      placeholder="e.g. 1024"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ px: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Temperature
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace'
                          }}
                        >
                          {temperatureValue.toFixed(1)}
                        </Typography>
                      </Box>
                      <Slider
                        value={temperatureValue}
                        min={0}
                        max={2}
                        step={0.1}
                        disabled={!canEdit}
                        onChange={(_, v) => handleChange('llm_temperature', v)}
                        valueLabelDisplay="auto"
                        marks={[
                          { value: 0, label: '0' },
                          { value: 0.5, label: '0.5' },
                          { value: 1, label: '1' },
                          { value: 1.5, label: '1.5' },
                          { value: 2, label: '2' }
                        ]}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canEdit || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>

                {!canEdit && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1, display: 'block' }}
                  >
                    View-only access. Contact an admin to edit.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default LLMSettings;

